import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from 'primereact/skeleton';

import Layout from '../../Layout/Layout';
import { useWallOverview, useLocationStart, useLocationStop, useLocationRefresh } from '../../../queries/wallOverview';
import { useStartWall, useStopWall, useResyncWall } from '../../../queries/videoWall';
import useWallSyncTelemetry from './useWallSyncTelemetry';
import WallLivePreview from './preview/WallLivePreview';
import ContentVerificationBanner from './preview/ContentVerificationBanner';
import FeatureLocked from '../../SharedComponents/FeatureLocked';

/**
 * Phase 7 — Video Wall dashboard (the Video Wall landing page). All locations with
 * their walls and live status: state, sync health (LAN/cloud + worst drift), cache,
 * now-playing + next scheduled transition, and one-click Start/Stop/Refresh per wall
 * and per location — no need to open each wall.
 */

const DRIFT_WARN_MS = 120;   // worst per-screen |drift| above this → "drifting"

const dot = (color) => (
  <span style={{ width: 10, height: 10, borderRadius: 999, background: color, display: 'inline-block', flexShrink: 0 }} />
);

function WallRow({ wall }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tel = useWallSyncTelemetry(wall.id);
  const start = useStartWall(wall.id);
  const stop = useStopWall(wall.id);
  const resync = useResyncWall(wall.id);

  // Live health from RTDB telemetry; fall back to the DB state for offline walls.
  const live = tel.state || wall.state;
  const playing = live === 'playing' || live === 'preparing';
  const screens = Object.values(tel.screens || {}).filter(Boolean);
  const worstDrift = screens.reduce((m, s) => Math.max(m, Math.abs(s.drift_ms ?? 0)), 0);
  const drifting = playing && screens.length > 0 && worstDrift > DRIFT_WARN_MS;
  const caching = (tel.cachingScreens ?? 0) > 0;
  // Content honesty: never show green if a screen reports it's showing an error /
  // stalling. content_status (no_content > buffering) outranks drift/caching.
  const erroring = playing && screens.some((s) => s.content_status === 'no_content');
  const buffering = playing && screens.some((s) => s.content_status === 'buffering');

  const color = !playing ? 'var(--pp-text-secondary)'
    : erroring ? 'var(--pp-danger, #ef4444)'
    : (caching || drifting || buffering) ? 'var(--pp-warning)'
    : 'var(--pp-success)';

  const now = wall.schedule?.now;
  const next = wall.schedule?.next;
  const sourceLabel = (s) => t(`video_wall.schedule.source_${s || 'none'}`);

  return (
    <div className="p-3 d-flex flex-wrap align-items-center gap-3"
      style={{ borderTop: '1px solid var(--pp-border)' }}>
      {/* mini live preview (3C) — only while playing */}
      {playing && (
        <div role="button" onClick={() => navigate(`/apps/video-wall/${wall.id}`)} style={{ cursor: 'pointer', flex: 'none' }}>
          <WallLivePreview wallId={wall.id} maxWidth={150} />
        </div>
      )}
      {/* status + identity */}
      <div className="d-flex align-items-center gap-2" style={{ minWidth: 200, flex: '1 1 220px', cursor: 'pointer' }}
        onClick={() => navigate(`/apps/video-wall/${wall.id}`)}>
        {dot(color)}
        <div style={{ minWidth: 0 }}>
          <div className="text-truncate d-flex align-items-center gap-2" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>
            <ContentVerificationBanner wallId={wall.id} dotOnly />
            {wall.name}
            <span className="ms-2" style={{ fontSize: 11, fontWeight: 500, color: 'var(--pp-text-secondary)' }}>
              {wall.rows}×{wall.columns}{wall.group_name ? ` · ${wall.group_name}` : ''}
            </span>
          </div>
          <small style={{ color: 'var(--pp-text-secondary)' }}>
            {playing ? t(`video_wall.dashboard.state_${erroring ? 'error' : buffering ? 'buffering' : drifting ? 'drifting' : caching ? 'caching' : 'playing'}`) : t('video_wall.dashboard.state_stopped')}
            {playing && tel.syncMode ? ` · ${t(`video_wall.sync_mode_${tel.syncMode}`)}` : ''}
            {playing && screens.length > 0 ? ` · ${t('video_wall.dashboard.drift', { ms: worstDrift })}` : ''}
            {` · ${t('video_wall.dashboard.devices', { n: wall.devices_count })}`}
          </small>
        </div>
      </div>

      {/* now / next */}
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        {now && now.source !== 'none' ? (
          <>
            <div className="text-truncate" style={{ color: 'var(--pp-text-primary)', fontSize: 13 }}>
              <i className="pi pi-play me-1" style={{ fontSize: 11, color: 'var(--pp-success)' }} />
              {now.playlist_name || '—'}
              <span className="ms-1" style={{ fontSize: 11, color: 'var(--pp-text-secondary)' }}>
                · {now.window ? `${now.window.start}–${now.window.end}` : sourceLabel(now.source)}
              </span>
            </div>
            <small style={{ color: 'var(--pp-text-secondary)' }}>
              {next
                ? t('video_wall.schedule.next_is', { name: next.playlist_name || '—', at: next.at, day: t(`video_wall.schedule.day_${next.at_day}`, next.at_day) })
                : t('video_wall.schedule.next_none')}
            </small>
          </>
        ) : (
          <small style={{ color: 'var(--pp-text-secondary)' }}>
            <i className="pi pi-ban me-1" />{t('video_wall.schedule.now_nothing')}
            {next ? ` · ${t('video_wall.schedule.next_is', { name: next.playlist_name || '—', at: next.at, day: t(`video_wall.schedule.day_${next.at_day}`, next.at_day) })}` : ''}
          </small>
        )}
      </div>

      {/* inline controls */}
      <div className="d-flex gap-1">
        {playing ? (
          <Button icon="pi pi-stop-circle" size="small" severity="danger" outlined rounded
            title={t('video_wall.stop')} loading={stop.isLoading} onClick={() => stop.mutate()} />
        ) : (
          <Button icon="pi pi-play" size="small" severity="success" rounded
            title={t('video_wall.start')} loading={start.isLoading} onClick={() => start.mutate({})} />
        )}
        <Button icon="pi pi-sync" size="small" outlined rounded
          title={t('video_wall.refresh')} loading={resync.isLoading} disabled={!playing} onClick={() => resync.mutate()} />
        <Button icon="pi pi-calendar" size="small" text rounded
          title={t('video_wall.schedule.manage')} onClick={() => navigate(`/apps/video-wall/schedule?wall=${wall.id}`)} />
      </div>
    </div>
  );
}

function LocationCard({ loc }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const start = useLocationStart();
  const stop = useLocationStop();
  const refresh = useLocationRefresh();
  const isUnassigned = !loc.location_id;

  return (
    <div className="mb-3" style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)', overflow: 'hidden' }}>
      <div className="p-3 d-flex flex-wrap align-items-center justify-content-between gap-2"
        style={{ background: 'var(--pp-bg-secondary)' }}>
        <div>
          <h6 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>
            <i className={`pi ${isUnassigned ? 'pi-inbox' : 'pi-map-marker'} me-2`} style={{ color: 'var(--pp-primary)' }} />
            {isUnassigned ? t('video_wall.dashboard.unassigned') : loc.location}
            <span className="ms-2" style={{ fontSize: 12, fontWeight: 500, color: 'var(--pp-text-secondary)' }}>
              {t('video_wall.dashboard.walls_n', { n: loc.walls.length })}{loc.timezone ? ` · ${loc.timezone}` : ''}
            </span>
          </h6>
        </div>
        {!isUnassigned && (
          <div className="d-flex gap-1">
            <Button label={t('video_wall.dashboard.start_all')} icon="pi pi-play" size="small" severity="success" outlined
              loading={start.isLoading} onClick={() => start.mutate(loc.location_id)} />
            <Button label={t('video_wall.dashboard.stop_all')} icon="pi pi-stop-circle" size="small" severity="danger" outlined
              loading={stop.isLoading} onClick={() => stop.mutate(loc.location_id)} />
            <Button icon="pi pi-sync" size="small" outlined title={t('video_wall.dashboard.refresh_all')}
              loading={refresh.isLoading} onClick={() => refresh.mutate(loc.location_id)} />
          </div>
        )}
      </div>
      {loc.walls.map((w) => <WallRow key={w.id} wall={w} />)}
    </div>
  );
}

export default function VideoWallDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: locations = [], isLoading, error } = useWallOverview();

  const totalWalls = locations.reduce((n, l) => n + l.walls.length, 0);

  if (error?.response?.status === 402) {
    return (
      <Layout>
        <FeatureLocked feature="video_wall" message={error.response.data?.message} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h2 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>
            <i className="pi pi-th-large me-2" style={{ color: 'var(--pp-primary)' }} />
            {t('video_wall.dashboard.title')}
          </h2>
          <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dashboard.subtitle')}</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button label={t('video_wall.dashboard.all_walls')} icon="pi pi-list" outlined onClick={() => navigate('/apps/video-wall/walls')} />
          <Button label={t('video_wall.schedule.title')} icon="pi pi-calendar" outlined onClick={() => navigate('/apps/video-wall/schedule')} />
          <Button label={t('video_wall.create_new')} icon="pi pi-plus" onClick={() => navigate('/apps/video-wall/create')} />
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex flex-column gap-3">{[...Array(2)].map((_, i) => <Skeleton key={i} height="8rem" />)}</div>
      ) : totalWalls === 0 ? (
        <div className="text-center p-5" style={{ background: 'var(--pp-bg-secondary)', border: '1px dashed var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
          <div style={{ fontSize: '3rem' }}>🧱</div>
          <h4 className="mt-3" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.dashboard.empty_title')}</h4>
          <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dashboard.empty_subtitle')}</p>
          <Button label={t('video_wall.create_new')} icon="pi pi-plus" onClick={() => navigate('/apps/video-wall/create')} />
        </div>
      ) : (
        locations.map((loc) => <LocationCard key={loc.location_id || '_none'} loc={loc} />)
      )}
    </Layout>
  );
}
